import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import CSSModule from 'react-css-modules';
import { ArrowRight, Search, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Spinner, SeoHead } from 'components';
import { actions as affiliateActions } from 'coupon-common/src/modules/affiliateBusiness';
import {
  actions as affiliateCouponActions,
  duck as affiliateCouponsDuck,
} from 'coupon-common/src/modules/businesses/affiliateCoupons';
import { COUPON_TYPES } from 'coupon-common';
import { SITE_URL } from 'components/SeoHead/SeoHead';

import LoadingLogo from '../../../components/LoadingLogo/LoadingLogo';

// Same fallback Home.js/CouponsMapAll use for coupons with no photo.
// ASSUMPTION: same relative depth as CouponsMapAll's own import of this
// asset — confirm/adjust the leading ../ count if this file's actual
// nesting differs.
import NoCouponImage from '../../../assets/images/NoCouponImage.webp';
import { RouteNames } from '../../../constants';
import styles from './AffiliateBusinessesListAll.module.scss';

// ASSUMPTION: getAffiliateBusinesses() is the same action used by the admin
// AffiliateBusinessManager page. That call takes no params and the reducer
// returns { results: [...] } with no pagination info, so this page does
// client-side search only (no "load more" / nextPage handling like
// BusinessesListAll). If a paginated / city-scoped public endpoint gets
// added later, wire it in the same way BusinessesListAll uses nextPage.
const AFFILIATE_COUPON_INITIAL_STATE = affiliateCouponsDuck.AFFILIATE_BUSINESS_COUPONS_INITIAL_STATE;

// Same logic as CouponsMapAll's getCouponDealAmount, so a coupon shows the
// identical "$5 off" / "20% off" / "150 points" subtitle everywhere.
const getCouponDealAmount = coupon => {
  if (coupon?.type === COUPON_TYPES.AMOUNT_OFF) return `$${coupon.amount_off} off`;
  if (coupon?.type === COUPON_TYPES.PERCENTAGE_OFF) return `${Math.round(coupon.percentage_off)}% off`;
  if (coupon?.type === COUPON_TYPES.POINTS_BACK) return `${coupon.points_back} points`;
  return null;
};

// ASSUMPTION: FullCouponListSerializer's inherited (BaseCouponListSerializer)
// fields include `image_url` as an array of { image_url } objects, same
// shape CouponsMapAll assumes for regular business coupons — adjust if the
// affiliate coupon payload's photo field is shaped differently.
const getCouponImage = coupon => {
  const images = coupon?.image_url;
  return Array.isArray(images) && images.length > 0 ? images[0].image_url : NoCouponImage;
};

const AffiliateBusinessesListAll = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const affiliateState = useSelector(state => state.affiliateBusiness) || {};
  const { fetching, fetched, data, error } = affiliateState;
  const list = (data && data.results) || [];

  // Keyed by business id — mirrors CouponsMapAll's businesses.coupons
  // pattern: state.business.affiliateCoupons[id] = {fetching, fetched, data, error}.
  const affiliateCouponsState = useSelector(state => state.businesses.affiliateCoupons) || {};

  // Keeps the loading overlay up until BOTH the business list itself has
  // resolved AND every business's coupon fetch has resolved — success or
  // error either count as "settled" (a single failed coupon fetch
  // shouldn't spin this forever). Also tracks a running count of resolved
  // deals for the progress bar/label.
  const businessListSettled = fetched || !!error;

  const { allCouponsSettled, settledCount, dealsFoundCount } = useMemo(() => {
    if (!businessListSettled) {
      return { allCouponsSettled: false, settledCount: 0, dealsFoundCount: 0 };
    }
    let settled = 0;
    let deals = 0;
    list.forEach(business => {
      const entry = affiliateCouponsState[business.id] || AFFILIATE_COUPON_INITIAL_STATE;
      const isSettled = entry.fetched || !!entry.error;
      if (!isSettled) return;
      settled += 1;
      if (entry.fetched) {
        const raw = entry.data;
        const coupons = Array.isArray(raw) ? raw : raw?.coupons || raw?.results || [];
        deals += coupons.length;
      }
    });
    return {
      allCouponsSettled: list.length === 0 || settled === list.length,
      settledCount: settled,
      dealsFoundCount: deals,
    };
  }, [businessListSettled, list, affiliateCouponsState]);

  const showLoadingOverlay = !allCouponsSettled;
  const loadingProgress = list.length > 0 ? Math.round((settledCount / list.length) * 100) : 0;


  const [searchKey, setSearchKey] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // Absolutely-positioned overlay instead of a grid-flow sibling — this is
  // what keeps every other card exactly where it is when a business gets
  // selected. gridRef anchors the positioning; cardRefs lets us measure
  // exactly where the selected card's bottom edge is so the shelf sits
  // right under it, "coming forward" over the row(s) below instead of
  // pushing them down.
  const gridRef = useRef(null);
  const cardRefs = useRef({});
  const [shelfTop, setShelfTop] = useState(0);

  useEffect(() => {
    dispatch(affiliateActions.getAffiliateBusinesses());
  }, [dispatch]);

  // Fetch every business's coupons up front (not on-demand per click) so
  // the list can sort coupon-having-first as soon as each response lands.
  // Per-id fetched/fetching guard (same shape CouponsMapAll uses for its
  // one selected business) run across the whole list at once on load.
  //
  // NOTE: this fires one request per business on mount — fine at current
  // list sizes, but worth revisiting (e.g. a backend coupon_count on the
  // list endpoint instead) if the partner list grows large.
  useEffect(() => {
    list.forEach(business => {
      const entry = affiliateCouponsState[business.id] || AFFILIATE_COUPON_INITIAL_STATE;
      if (entry.fetched || entry.fetching) return;
      dispatch(affiliateCouponActions.getDetail(business.id));
    });
    // affiliateCouponsState intentionally omitted — the guard above already
    // prevents duplicate dispatches, and re-running this whenever any
    // business's coupons resolve isn't needed since every business was
    // already covered on the first pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, dispatch]);

  const goToDetails = useCallback(id => navigate(`${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${id}`), [navigate]);

  const getBusinessCoupons = useCallback(
    businessId => {
      const entry = affiliateCouponsState[businessId] || AFFILIATE_COUPON_INITIAL_STATE;
      const raw = entry.data;
      // ASSUMPTION: AffiliateOrFlyerCouponsView returns a bare array
      // (serializer.data straight off get_serializer(queryset, many=True))
      // — fall back to {coupons:[...]} / {results:[...]} in case that changes.
      const coupons = Array.isArray(raw) ? raw : raw?.coupons || raw?.results || [];
      return { ...entry, coupons };
    },
    [affiliateCouponsState],
  );

  const selectBusiness = useCallback(id => {
    setSelectedId(prev => (prev === id ? null : id));
  }, []);

  const openCoupon = coupon => {
    const target = coupon?.affiliate_link || coupon?.business_web_url;
    if (!target) return;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const filteredBusinesses = useMemo(() => {
    let result = list;
    if (searchKey.trim()) {
      const lowerSearchKey = searchKey.toLowerCase();
      result = result.filter(business => business.name?.toLowerCase().includes(lowerSearchKey));
    }
    // Two-tier stable sort: any business with at least one coupon stays
    // ahead of every business with none, but WITHIN each tier the original
    // list order is kept rather than re-ranked by raw count — otherwise the
    // list visibly reshuffles as each business's coupon fetch resolves at
    // a different time (a 2-coupon business briefly outranking one that
    // ends up with 5, then dropping once that fetch lands).
    return [...result].sort((a, b) => {
      const aHasCoupons = getBusinessCoupons(a.id).coupons.length > 0;
      const bHasCoupons = getBusinessCoupons(b.id).coupons.length > 0;
      if (aHasCoupons === bHasCoupons) return 0;
      return aHasCoupons ? -1 : 1;
    });
  }, [list, searchKey, getBusinessCoupons]);

  const selectedBusiness = useMemo(
    () => filteredBusinesses.find(b => b.id === selectedId) || null,
    [filteredBusinesses, selectedId],
  );
  const selectedCoupons = selectedBusiness ? getBusinessCoupons(selectedBusiness.id) : null;

  const measureShelfPosition = useCallback(() => {
    if (!selectedId || !gridRef.current) return;
    const card = cardRefs.current[selectedId];
    if (!card) return;
    const gridRect = gridRef.current.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    setShelfTop(cardRect.bottom - gridRect.top + 16);
  }, [selectedId]);

  useLayoutEffect(() => {
    measureShelfPosition();
  }, [measureShelfPosition]);

  // Grid columns reflow at different viewport widths (auto-fill), which can
  // move the selected card to a different row — recompute on resize so the
  // overlay doesn't end up floating over the wrong row.
  useEffect(() => {
    window.addEventListener('resize', measureShelfPosition);
    return () => window.removeEventListener('resize', measureShelfPosition);
  }, [measureShelfPosition]);

  const getInitials = name => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
    return initials.toUpperCase();
  };

  // Deterministic accent color per business, used as the fallback badge
  // when a business has no image.
  const badgeVariant = name => {
    if (!name) return 0;
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % 5;
  };

  // ─── SEO ────────────────────────────────────────────────────────────
  const pagePath = RouteNames.AFFILIATE_BUSINESSES_LIST_ALL;
  const seoTitle = "Top Online Promo Codes & Brands | My Couponbook";
  const seoDescription = "Search verified promo codes and discounts from top online brands across Canada. Find the best online deals and start saving money today.";

  // const seoTitle = 'Our Partner Stores';
  // const seoDescription =
  //   'Shop our affiliate partner stores and stack their offers with My Couponbook deals and coupons.';
  const structuredData =
    list && list.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: list.slice(0, 50).map((business, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: business.name,
            url: `${SITE_URL}${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${business.id}`,
          })),
        }
      : undefined;

  return (
    <div styleName="page">
            {showLoadingOverlay && (
        <div styleName="loading-overlay">
          <LoadingLogo
            size={96}
            label="Loading partner deals…"
            progress={loadingProgress}
            progressLabel={`${dealsFoundCount} ${dealsFoundCount === 1 ? 'deal' : 'deals'} found · ${settledCount}/${list.length} stores`}
          />
        </div>
      )}
      <SeoHead title={seoTitle} description={seoDescription} path={pagePath} type="website" structuredData={structuredData} />

      <div styleName="wrap">
        <div styleName="hero">
          <p styleName="eyebrow">Partner Deals</p>
          <h1 styleName="heading">Find the best deals from our online brands.</h1>
          <p styleName="subheading">
            Every store below is a My Couponbook partner. Visit their site directly, then bring your savings back home.
          </p>
          <p styleName="disclosure">
            My Couponbook may receive payment for purchases made through links to these partner stores.
          </p>
        </div>

        <div styleName="search-bar-wrap">
          <Search size={18} styleName="search-icon" />
          <input
            type="text"
            placeholder="Search partner stores"
            value={searchKey}
            onChange={e => setSearchKey(e.target.value)}
            styleName="search-input"
          />
          {searchKey.length > 0 && (
            <button type="button" styleName="clear-btn" onClick={() => setSearchKey('')} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </div>

        {error && <p styleName="state-message">Something went wrong loading partner stores.</p>}

        {!error && !fetched && fetching && (
          <div styleName="state-message">
            <Spinner show size="md" />
          </div>
        )}

        {!error && (!list || list.length === 0) && fetched && (
          <p styleName="state-message">No partner stores yet — check back soon.</p>
        )}

        {!error && filteredBusinesses.length === 0 && list.length > 0 && fetched && (
          <p styleName="state-message">No stores match "{searchKey}".</p>
        )}

        {!error && filteredBusinesses.length > 0 && (
          <div styleName="grid" ref={gridRef}>
            {filteredBusinesses.map(item => {
              const isSelected = item.id === selectedId;
              const { coupons } = getBusinessCoupons(item.id);

              return (
                <button
                  key={item.id}
                  type="button"
                  ref={el => (cardRefs.current[item.id] = el)}
                  styleName={isSelected ? 'card card-selected' : 'card'}
                  onClick={() => selectBusiness(item.id)}
                >
                  {coupons.length > 0 && (
                    <span styleName="card-coupon-badge">
                      {coupons.length} {coupons.length === 1 ? 'offer' : 'offers'}
                    </span>
                  )}
                  {item.logo ? (
                    <span styleName="card-image-wrap">
                      <img src={item.logo} alt={item.name} styleName="card-image" />
                    </span>
                  ) : (
                    <span styleName={`badge badge-${badgeVariant(item.name)}`}>{getInitials(item.name)}</span>
                  )}
                  <span styleName="card-name">{item.name}</span>
                  <span styleName="card-cta">
                    {isSelected ? 'Hide coupons' : 'View coupons'}
                    <ArrowRight
                      size={14}
                      styleName={isSelected ? 'card-cta-chevron card-cta-chevron-open' : 'card-cta-chevron'}
                    />
                  </span>
                </button>
              );
            })}

            {selectedBusiness && (
              <div styleName="shelf" style={{ top: shelfTop }}>
                <div styleName="shelf-header">
                  <h3 styleName="shelf-title">{selectedBusiness.name}</h3>
                  <div styleName="shelf-header-actions">
                    <button type="button" styleName="shelf-visit-btn" onClick={() => goToDetails(selectedBusiness.id)}>
                      View business
                    </button>
                    <button
                      type="button"
                      styleName="shelf-close-btn"
                      onClick={() => setSelectedId(null)}
                      aria-label="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {selectedCoupons.fetching && !selectedCoupons.fetched && <Spinner show size="sm" />}

                {selectedCoupons.error && <p styleName="shelf-state-text">Couldn't load coupons for this store.</p>}

                {!selectedCoupons.fetching &&
                  !selectedCoupons.error &&
                  selectedCoupons.fetched &&
                  selectedCoupons.coupons.length === 0 && (
                    <p styleName="shelf-state-text">No active coupons for this store right now.</p>
                  )}

                {selectedCoupons.coupons.length > 0 && (
                  <div styleName="shelf-coupons">
                    {selectedCoupons.coupons.map(coupon => (
                      <button key={coupon.id} type="button" styleName="coupon-card" onClick={() => openCoupon(coupon)}>
                        <img src={getCouponImage(coupon)} alt="" styleName="coupon-card-img" />
                        <div styleName="coupon-card-overlay" />
                        <div styleName="coupon-card-text">
                          <p styleName="coupon-card-title">{coupon.title || 'Special Offer'}</p>
                          <p styleName="coupon-card-subtitle">
                            {getCouponDealAmount(coupon) || coupon.description || 'Tap to view details'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSSModule(AffiliateBusinessesListAll, styles, { allowMultiple: true });